import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { API_CONFIG } from '@/constants/api';
import { getAuthToken } from '@/utils/auth';
import { 
  Bell, 
  X, 
  Check, 
  User, 
  Stethoscope,
  AlertCircle,
  Video,
  Home
} from 'lucide-react';

interface OnDemandMatchListenerProps {
  userId: string;
}

export const OnDemandMatchListener: React.FC<OnDemandMatchListenerProps> = ({ userId }) => {
  const navigate = useNavigate();
  const [proposal, setProposal] = useState<{
    queueId: string;
    patientId: string;
    reason: string;
    symptoms?: string;
    mode: 'TELEMEDICINE' | 'HOME_CARE';
  } | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    const token = getAuthToken();
    const newSocket = io(`${API_CONFIG.BASE_URL.replace('/api', '')}/consultation`, {
      query: { userId, token },
      transports: ['websocket'],
    });

    newSocket.on('matchProposed', (data: { 
      queueId: string; 
      patientId: string; 
      reason: string; 
      symptoms?: string;
      mode: 'TELEMEDICINE' | 'HOME_CARE';
    }) => {
      console.log('Match proposed:', data);
      setProposal(data);
      // Play a notification sound if possible
      try {
        const audio = new Audio('/notification-sound.mp3'); // Opcional
        audio.play();
      } catch (e) {}
    });

    newSocket.on('matchConfirmed', (data: { appointmentId: string }) => {
      setProposal(null);
      navigate(`/chat/${data.appointmentId}`);
    });

    newSocket.on(
      'matchCancelled',
      (data: { queueId?: string; patientId?: string; reason?: string }) => {
        console.log('Match cancelled:', data);
        setProposal((current) => {
          if (!current) return null;
          if (data.queueId && current.queueId !== data.queueId) return current;
          return null;
        });
      },
    );

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [userId, navigate]);

  const handleAccept = () => {
    if (!socket || !proposal) return;
    socket.emit('acceptOnDemandMatch', { 
      doctorId: userId, 
      queueId: proposal.queueId 
    });
  };

  const handleReject = () => {
    if (!socket || !proposal) return;
    socket.emit('rejectOnDemandMatch', { 
      doctorId: userId, 
      queueId: proposal.queueId 
    });
    setProposal(null);
  };

  if (!proposal) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white dark:bg-neutral-900 rounded-3xl w-full max-w-md shadow-2xl border border-teal-100 dark:border-teal-900/50 overflow-hidden animate-in zoom-in-95 duration-300">
        <div className="bg-linear-to-r from-teal-500 to-teal-600 p-6 text-white relative">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-md">
              <Bell className="w-6 h-6 animate-bounce" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Nova Consulta Urgente</h3>
              <p className="text-teal-50 text-xs">Paciente aguardando no plantão</p>
            </div>
          </div>
          <button 
            onClick={handleReject}
            className="absolute top-6 right-6 p-1 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-8">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-neutral-800 rounded-2xl border border-gray-100 dark:border-neutral-700 flex flex-col items-center text-center gap-2">
                {proposal.mode === 'TELEMEDICINE' ? (
                  <>
                    <Video className="w-6 h-6 text-blue-500" />
                    <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Modalidade</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">Telemedicina</p>
                  </>
                ) : (
                  <>
                    <Home className="w-6 h-6 text-purple-500" />
                    <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Modalidade</p>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">Domiciliar</p>
                  </>
                )}
              </div>
              <div className="p-4 bg-gray-50 dark:bg-neutral-800 rounded-2xl border border-gray-100 dark:border-neutral-700 flex flex-col items-center text-center gap-2">
                <User className="w-6 h-6 text-teal-600" />
                <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Paciente</p>
                <p className="text-sm font-bold text-gray-900 dark:text-white">Aguardando...</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-neutral-800 rounded-2xl border border-gray-100 dark:border-neutral-700">
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Motivo</p>
                <p className="text-gray-900 dark:text-white font-semibold">{proposal.reason}</p>
              </div>
            </div>

            {proposal.symptoms && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 uppercase font-bold tracking-wider flex items-center gap-2">
                  <Stethoscope className="w-3 h-3" /> Sintomas Relatados
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-neutral-800 p-4 rounded-xl italic">
                  "{proposal.symptoms}"
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <AlertCircle className="w-4 h-4" />
              <p className="text-[10px] font-medium uppercase tracking-tighter">Atenção: Ao aceitar, você será redirecionado para a consulta.</p>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                onClick={handleReject}
                className="flex-1 py-4 px-6 border border-gray-200 dark:border-neutral-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-neutral-800 rounded-2xl font-bold transition-all"
              >
                Recusar
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 py-4 px-6 bg-teal-600 hover:bg-teal-700 text-white rounded-2xl font-bold shadow-lg shadow-teal-500/20 transition-all flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                Aceitar Agora
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
